/**
 * Humanize internal enum codes into user-facing labels.
 *
 * Source of truth for label conversion across the Jurisdiction Management
 * workspace (Tax Collectors, Tax Categories, Collector Corrections). Internal
 * codes stay in the data layer; this utility surfaces business-friendly
 * presentation strings.
 *
 * Conventions:
 *  - Inputs are MongoDB-stored snake_case strings.
 *  - Outputs use Title Case with hyphens for compound modifiers
 *    (e.g. "Third-Party Agency", not "Third Party Agency").
 *  - Unknown codes fall back to a safe `titleCase(snake_case)` conversion.
 */

const COLLECTOR_TYPE_LABELS = {
  county_treasurer:      'County Treasurer',
  county_tax_claim:      'County Tax Claim Bureau',
  municipal_collector:   'Municipal Collector',
  school_collector:      'School Collector',
  third_party_agency:    'Third-Party Agency',
  internal_revenue_dept: 'Internal Revenue Department',
  other:                 'Other',
};

const COLLECTOR_TYPE_ACCENT = {
  county_treasurer:      'badge-blue',
  county_tax_claim:      'badge-purple',
  municipal_collector:   'badge-green',
  school_collector:      'badge-amber',
  third_party_agency:    'badge-teal',
  internal_revenue_dept: 'badge-pink',
  other:                 'badge-gray',
};

/**
 * Titlecase a snake_case identifier as a generic fallback.
 *   "special_assessment_lighting" → "Special Assessment Lighting"
 */
function snakeToTitle(s) {
  if (s === null || s === undefined) return '';
  return String(s)
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function humanizeCollectorType(code) {
  if (!code) return '—';
  return COLLECTOR_TYPE_LABELS[code] || snakeToTitle(code);
}

export function collectorTypeBadgeClass(code) {
  return COLLECTOR_TYPE_ACCENT[code] || 'badge-gray';
}

/**
 * Tax category presentation:
 *   - Prefer the row's `display_name` when set (it is the business label).
 *   - Otherwise fall back to humanized code.
 */
export function humanizeTaxCategory(row) {
  if (!row) return '—';
  if (row.display_name) return row.display_name;
  return snakeToTitle(row.code || '');
}

export { snakeToTitle };
